/**
 * Where on the airframe a round that connected actually landed.
 *
 * AD-007d names the component set — "engine, fuel tank, left/right wing cells,
 * tail/control surfaces, pilot" — and says hits are distributed *by aspect*.
 * The engine's own damage model (flight/damage.ts) owns five of those six;
 * the pilot is physiology's. Nothing in the game data gives a distribution, so
 * the table below is a port-side invention and is labelled as one: it encodes
 * only the geometry any reader would agree with (you cannot shoot the engine
 * off a target's tail, a beam attack puts rounds through the near wing cell,
 * the pilot sits under the top decking).
 */

import type { ComponentId } from "../flight/damage.ts";
import type { RngBank, RngStreamName } from "../flight/rng.ts";
import type { Tunables } from "../flight/tunables.ts";
import { rollDamage, roundDamage } from "./ballistics.ts";
import type { RoundImpact } from "./collide.ts";
import { HIT_ASPECTS, type HitAspect } from "./geometry.ts";

/** The five airframe components plus the pilot — AD-007d's list. */
export const HIT_TARGETS = ["engine", "fuel", "wingLeft", "wingRight", "controls", "pilot"] as const;

export type HitTarget = (typeof HIT_TARGETS)[number];

export function isComponent(target: HitTarget): target is ComponentId {
  return target !== "pilot";
}

/**
 * Relative weights per aspect. Rows sum to 100 for readability only — the
 * picker normalizes. INFERRED, port-side: see the module note.
 */
export const ASPECT_HIT_WEIGHTS: Readonly<Record<HitAspect, Readonly<Record<HitTarget, number>>>> = {
  nose: { engine: 40, fuel: 15, wingLeft: 12, wingRight: 12, controls: 9, pilot: 12 },
  tail: { engine: 10, fuel: 18, wingLeft: 11, wingRight: 11, controls: 28, pilot: 22 },
  left: { engine: 16, fuel: 14, wingLeft: 38, wingRight: 8, controls: 12, pilot: 12 },
  right: { engine: 16, fuel: 14, wingLeft: 8, wingRight: 38, controls: 12, pilot: 12 },
  top: { engine: 12, fuel: 18, wingLeft: 18, wingRight: 18, controls: 10, pilot: 24 },
  belly: { engine: 24, fuel: 26, wingLeft: 16, wingRight: 16, controls: 12, pilot: 6 },
};

/**
 * One weighted draw, on the `gunnery` stream by default. Ground fire passes
 * `"flak"` instead: AD-007a's whole point is that one subsystem's draws cannot
 * move another's, and a battery allocating a hit must not shift which wing a
 * dogfight takes off next.
 */
export function allocateHit(aspect: HitAspect, rng: RngBank, stream: RngStreamName = "gunnery"): HitTarget {
  const weights = ASPECT_HIT_WEIGHTS[aspect];
  const index = rng.weightedIndex(
    stream,
    HIT_TARGETS.map((target) => weights[target]),
  );
  return HIT_TARGETS[index] ?? "controls";
}

/** Every aspect the classifier can produce is a row here — asserted by the tests, kept honest here. */
export function aspectsCovered(): boolean {
  return HIT_ASPECTS.every((aspect) => aspect in ASPECT_HIT_WEIGHTS);
}

/** One round's worth of damage, landed on one component of one aircraft. */
export interface RoundHit {
  readonly shooterId: string;
  readonly targetId: string;
  readonly component: HitTarget;
  readonly amount: number;
  /** Metres the round had flown when it struck. */
  readonly rangeM: number;
  readonly aspect: HitAspect;
}

/**
 * Turns a geometric impact into a hit: roll how much it took off, then roll
 * where it landed. Two draws on the `gunnery` stream, always in that order —
 * the magnitude first, because that is the die the original throws and the
 * component allocation is this port's own addition on top of it.
 *
 * `stream` moves both draws onto another stream for a shooter that is not an
 * aeroplane: `"airship"` for a Zeppelin's gun stations, the same isolation flak
 * has always had.
 */
export function hitFromImpact(
  impact: RoundImpact,
  rng: RngBank,
  tunables: Tunables,
  stream: RngStreamName = "gunnery",
): RoundHit {
  const base = roundDamage(impact.round.damageRaw, impact.rangeM, tunables);
  return {
    shooterId: impact.round.shooterId,
    targetId: impact.targetId,
    amount: rollDamage(rng, base, tunables, stream),
    component: allocateHit(impact.aspect, rng, stream),
    rangeM: impact.rangeM,
    aspect: impact.aspect,
  };
}
